import type { FetchListOptions, ListResult, PaginatedQuery } from "@shared/types/list";

/** 后端 list item（camelCase，与 UserListItemData 对齐） */
export interface UserListApiItem {
    id: string;
    corpId: string;
    corpName?: string;
    identity: number;
    identityLabel?: string;
    username: string;
    tel: string;
    email: string;
    nick: string;
    status: number;
    statusLabel?: string;
    creatorId: string;
    creatorName?: string;
    loginCount?: number;
    lastLoginIp?: string | null;
    lastLoginTime?: number | null;
    ipProvince?: string;
    ipCity?: string;
    createTime?: string;
    roleIds: string[];
    roleNames?: string[];
}

/** 列表行（页面 / 表格） */
export interface SystemUserRow {
    id: string;
    corpId: string;
    corpName: string;
    username: string;
    nick: string;
    tel: string;
    email: string;
    roleIds: string[];
    roleNames: string[];
    /** 1 启用 2 禁用 */
    status: number;
    statusLabel: string;
    loginCount: number;
    lastLoginIp: string;
    ipProvince: string;
    ipCity: string;
    /** 已格式化为展示字符串 */
    lastLoginTime: string;
    creatorName: string;
    createTime: string;
}

/** add / resetPassword 一次性明文凭据 */
export interface UserCredential {
    username: string;
    account: string;
    password: string;
}

export interface UserAddPayload {
    username: string;
    passwordMode: number;
    password?: string;
    nick: string;
    tel: string;
    email?: string;
    roleIds?: string[];
}

export interface UserEditPayload {
    id: string;
    username: string;
    nick: string;
    tel: string;
    email?: string;
    roleIds?: string[];
}

export interface UserResetPasswordPayload {
    id: string;
    passwordMode: number;
    password?: string;
}

export interface SystemUserQueryParam extends PaginatedQuery {
    status: number | "";
    roleIds: string[];
    username: string;
    nick: string;
    /** Admin 列表可选企业 */
    corpId?: string | "";
}

export type UserManagementMode = "full" | "jumpOnly";

/** 用户登录日志列表项（与 UserLoginLogListItemData 对齐） */
export interface UserLoginLogListItem {
    id: string;
    loginName: string;
    loginIp: string;
    browser: string;
    os: string;
    loginAt: number;
    loginAtLabel: string;
    status: number;
    statusLabel: string;
    source: string;
    ipCountry: string;
    ipProvince: string;
    ipCity: string;
    ipIsp: string;
}

export function mapUserLoginLogListItem(item: UserLoginLogListItem): UserLoginLogListItem {
    return {
        ...item,
        id: item.id == null ? "" : String(item.id),
    };
}

export interface UserLoginLogsQuery {
    userId: string;
    page: number;
    limit: number;
}

/** 列表拉取（full / jumpOnly 共用） */
export interface UserListApi {
    fetchList: (params: SystemUserQueryParam, options?: FetchListOptions) => Promise<ListResult<SystemUserRow>>;
}

/** 角色下拉（筛选 / 表单；无则不展示系统角色占位） */
export interface UserRoleOptionItem {
    id: string;
    name: string;
}

/** 壳注入：manage / customer 完整 CRUD */
export interface UserManagementApi extends UserListApi {
    add: (payload: UserAddPayload) => Promise<UserCredential>;
    edit: (payload: UserEditPayload) => Promise<void>;
    resetPassword: (payload: UserResetPasswordPayload) => Promise<UserCredential>;
    setStatus: (ids: string[], status: number) => Promise<void>;
    delete: (id: string) => Promise<void>;
    fetchLoginLogs: (params: UserLoginLogsQuery, options?: FetchListOptions) => Promise<ListResult<UserLoginLogListItem>>;
    /** 可选：真实角色选项；不提供则不展示角色筛选/表单角色 */
    fetchRoleOptions?: () => Promise<UserRoleOptionItem[]>;
}

/** Admin 只读 + 进入客户端 */
export interface UserJumpOnlyApi extends UserListApi {
    jumpLogin: (userId: string) => Promise<{ ticket: string }>;
}
